import { FC, ReactNode } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { humanizeChartLabel } from '../chartPresentation';

interface CustomNivoChartTooltipEntry {
    color?: string;
    label: ReactNode;
    value: number | string | null | undefined;
}

interface CustomNivoChartTooltipBar {
    color?: string;
    data?: Record<string, unknown>;
    id?: number | string;
    indexBy?: string;
    indexValue?: ReactNode;
    value?: number | string | null;
}

interface CustomNivoChartTooltipProps {
    bar?: CustomNivoChartTooltipBar;
    entries?: CustomNivoChartTooltipEntry[];
    formatValue?: (value: number | string | null | undefined) => string;
    labelByKey?: Record<string, string>;
    title?: ReactNode;
}

const defaultFormatValue = (value: number | string | null | undefined) =>
    typeof value === 'number' ? value.toLocaleString() : String(value ?? '');

const CustomNivoChartTooltip: FC<CustomNivoChartTooltipProps> = ({
    bar,
    entries,
    formatValue = defaultFormatValue,
    labelByKey = {},
    title,
}) => {
    const { token } = useAntDesignTheme();

    const { id, value, indexValue, color, data = {}, indexBy } = bar ?? {};

    const barEntries = Object.keys(data)
        .filter((key) => key !== indexBy)
        .map((key) => ({
            key,
            value: data[key],
        }))
        .filter((entry): entry is { key: string; value: number } => typeof entry.value === 'number');

    const sorted = [...barEntries].sort((a, b) => b.value - a.value);

    const resolvedEntries = entries ?? [
        {
            color,
            label: id === undefined ? '' : (labelByKey[String(id)] ?? id),
            value,
        },
    ];

    return (
        <div
            style={{
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                padding: '0.5rem 0.75rem',
                boxShadow: token.boxShadowSecondary,
                minWidth: 160,
                maxWidth: 320,
            }}
        >
            {/* Title (dimension) */}
            <div
                style={{
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    marginBottom: 6,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {title ?? indexValue}
            </div>

            {/* Primary hovered value */}
            {resolvedEntries.map((entry, index) => (
                <div
                    key={`${String(entry.label)}-${index}`}
                    style={{
                        alignItems: 'center',
                        display: 'grid',
                        fontSize: 14,
                        fontWeight: 600,
                        gap: 8,
                        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                        marginBottom: index === resolvedEntries.length - 1 && sorted.length <= 1 ? 0 : 6,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <span
                        style={{
                            background: entry.color,
                            borderRadius: '50%',
                            height: 8,
                            width: 8,
                        }}
                    />
                    <span
                        style={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {humanizeChartLabel(labelByKey[String(entry.label)] ?? entry.label)}
                    </span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatValue(entry.value)}</span>
                </div>
            ))}

            {/* Multi-series breakdown */}
            {sorted.length > 1 && (
                <div
                    style={{
                        borderTop: `1px solid ${token.colorBorderSecondary}`,
                        marginTop: 6,
                        paddingTop: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    }}
                >
                    {sorted.map((entry) => (
                        <div
                            key={entry.key}
                            style={{
                                alignItems: 'center',
                                display: 'grid',
                                fontSize: 12,
                                gap: 12,
                                gridTemplateColumns: 'minmax(0, 1fr) auto',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <span
                                style={{
                                    color: token.colorTextSecondary,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {humanizeChartLabel(labelByKey[entry.key] ?? entry.key)}
                            </span>
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatValue(entry.value)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CustomNivoChartTooltip;
